import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { saveToCloud } from '../utils/api'

// 防抖计时器
let syncTimeout = null

// 辅助函数：生成 ID（回退方案）
const generateId = () => {
  try {
    return crypto.randomUUID()
  } catch (e) {
    // 针对非 HTTPS 等不支持 randomUUID 的环境的回退方案
    return 'yc-' + Math.random().toString(36).substring(2, 9)
  }
}

export const useStore = create(
  persist(
    (set, get) => ({
      // 内部状态
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),

      // 用户数据
      userId: null,
      answers: {},
      progress: 0,
      lastUpdated: null,
      darkMode: false,
      isSyncing: false,
      lastSyncTime: null,
      fieldMetadata: {}, // { [fieldKey]: timestamp }

      // 重置数据
      resetData: (newUserId) => {
        console.log(`[Store] 重置数据, 新用户 ID: ${newUserId}`)
        set({
          userId: newUserId,
          answers: {},
          fieldMetadata: {},
          progress: 0,
          lastUpdated: new Date().toISOString(),
          lastSyncTime: null
        })
      },

      // 初始化
      initStore: (userId) => {
        const currentUserId = get().userId
        if (userId && userId !== currentUserId) {
          get().resetData(userId)
        } else if (!currentUserId) {
          const newId = userId || generateId()
          console.log(`[Store] 初始化新 ID: ${newId}`)
          set({ userId: newId })
        }

        set({
          lastUpdated: new Date().toISOString()
        })

        // 设置页面失焦时立即同步
        if (typeof window !== 'undefined') {
          window.addEventListener('blur', () => {
            get().syncToCloudNow()
          })

          // 页面关闭前同步
          window.addEventListener('beforeunload', () => {
            get().syncToCloudNow()
          })
        }
      },

      // 同步到云端（防抖）
      syncToCloud: () => {
        // 清除之前的定时器
        if (syncTimeout) {
          clearTimeout(syncTimeout)
        }

        // 3秒后执行同步
        syncTimeout = setTimeout(() => {
          get().syncToCloudNow()
        }, 3000)
      },

      // 立即同步到云端
      syncToCloudNow: async () => {
        const { userId, answers, progress, lastUpdated, fieldMetadata, isSyncing } = get()

        // 如果正在同步，跳过
        if (isSyncing) {
          return
        }

        // 如果没有数据，跳过
        if (!userId || Object.keys(answers).length === 0) {
          return
        }

        set({ isSyncing: true })

        try {
          const data = {
            userId,
            answers,
            progress,
            lastUpdated,
            fieldMetadata,
            version: '2025-2026'
          }

          console.log(`[Sync] 正在同步数据到云端 (ID: ${userId})...`)
          await saveToCloud(userId, data)

          set({
            lastSyncTime: new Date().toISOString()
          })

          console.log('✅ 数据已同步到云端')
        } catch (error) {
          console.error('❌ 同步失败:', error)
        } finally {
          set({ isSyncing: false })
        }
      },

      // 设置答案
      setAnswer: (key, value) => {
        const now = new Date().toISOString()
        set((state) => ({
          answers: {
            ...state.answers,
            [key]: value
          },
          fieldMetadata: {
            ...state.fieldMetadata,
            [key]: now
          },
          lastUpdated: now
        }))
        get().updateProgress()
        // 确保进度更新后同步
        get().syncToCloud()
      },

      // 批量设置答案
      setAnswers: (newAnswers) => {
        const now = new Date().toISOString()
        const newMetadata = {}
        Object.keys(newAnswers).forEach(key => {
          newMetadata[key] = now
        })

        set((state) => ({
          answers: {
            ...state.answers,
            ...newAnswers
          },
          fieldMetadata: {
            ...state.fieldMetadata,
            ...newMetadata
          },
          lastUpdated: now
        }))
        get().updateProgress()
        get().syncToCloud()
      },

      // 获取答案
      getAnswer: (key, defaultValue = '') => {
        return get().answers[key] || defaultValue
      },

      // 更新进度
      updateProgress: () => {
        const answers = get().answers
        // 估算的总问题数，包括 12 个月、生活领域、总结句、规划等
        // 实际上项目中有约 70+ 个潜在输入点
        const totalQuestions = 75
        const answeredQuestions = Object.values(answers).filter(
          (answer) => answer && answer.toString().trim() !== ''
        ).length

        let progress = Math.round((answeredQuestions / totalQuestions) * 100)
        // 限制在 0-100% 之间
        progress = Math.min(100, Math.max(0, progress))
        set({ progress })
      },

      // 清除所有数据
      clearAll: () => {
        if (confirm('确定要清除所有数据吗？此操作不可恢复。')) {
          set({
            answers: {},
            progress: 0,
            lastUpdated: new Date().toISOString()
          })
        }
      },

      // 导出数据
      exportData: () => {
        const data = {
          userId: get().userId,
          version: '2025-2026',
          answers: get().answers,
          progress: get().progress,
          lastUpdated: get().lastUpdated
        }
        return data
      },

      // 导入数据（智能合并）
      importData: (cloudData, force = false) => {
        if (!cloudData || !cloudData.answers) return

        const localData = get()
        const localUserId = localData.userId
        const cloudUserId = cloudData.userId

        // 如果云端 ID 与本地 ID 不同，且不是首次初始化，说明是切换用户
        // 这种情况下必须“全量覆盖”而非“合并”，以防止数据混乱
        const isUserSwitch = cloudUserId && localUserId && cloudUserId !== localUserId

        if (force || isUserSwitch) {
          console.log(`[Store] 执行全量数据覆盖 (原因: ${force ? '强制' : '用户切换'})`)
          set({
            userId: cloudUserId || localUserId,
            answers: cloudData.answers || {},
            fieldMetadata: cloudData.fieldMetadata || {},
            progress: cloudData.progress || 0,
            lastUpdated: cloudData.lastUpdated || new Date().toISOString(),
            lastSyncTime: new Date().toISOString()
          })
          return
        }

        const localAnswers = localData.answers
        const localMetadata = localData.fieldMetadata || {}
        const cloudAnswers = cloudData.answers
        const cloudMetadata = cloudData.fieldMetadata || {}

        const mergedAnswers = { ...localAnswers }
        const mergedMetadata = { ...localMetadata }
        let hasChanges = false

        // 获取所有涉及的 key
        const allKeys = new Set([...Object.keys(localAnswers), ...Object.keys(cloudAnswers)])

        allKeys.forEach(key => {
          const cloudVal = cloudAnswers[key]
          const localVal = localAnswers[key]
          const cloudTime = cloudMetadata[key]
          const localTime = localMetadata[key]

          // 合并策略：
          // 1. 如果云端有值，本地没值 -> 取云端
          // 2. 如果云端有时间戳，本地也有时间戳 -> 取较新的那个
          // 3. 如果云端有时间戳，本地没时间戳（旧数据） -> 且值不同 -> 取云端
          // 4. 如果值完全相同 -> 无需操作

          if (cloudVal === localVal) return

          let shouldTakeCloud = false

          if (cloudVal && !localVal) {
            // 本地无，云端有
            shouldTakeCloud = true
          } else if (cloudTime && localTime) {
            // 都有时间戳，比较先后
            if (new Date(cloudTime) > new Date(localTime)) {
              shouldTakeCloud = true
            }
          } else if (cloudVal && !localTime) {
            // 本地没时间戳（老版本数据），优先信任云端（假设它是多端同步来的）
            // 但如果本地值更长且云端也是老数据，则保持本地（简单启发式）
            shouldTakeCloud = true
          }

          if (shouldTakeCloud) {
            mergedAnswers[key] = cloudVal
            mergedMetadata[key] = cloudTime || cloudData.lastUpdated || new Date().toISOString()
            hasChanges = true
          }
        })

        if (hasChanges) {
          set({
            answers: mergedAnswers,
            fieldMetadata: mergedMetadata,
            lastUpdated: cloudData.lastUpdated || new Date().toISOString()
          })
          get().updateProgress()
        }
      },

      // 切换暗黑模式
      toggleDarkMode: () => {
        set((state) => ({ darkMode: !state.darkMode }))
        const isDark = !get().darkMode
        document.documentElement.classList.toggle('dark', isDark)
      },

      // 设置暗黑模式
      setDarkMode: (isDark) => {
        set({ darkMode: isDark })
        document.documentElement.classList.toggle('dark', isDark)
      }
    }),
    {
      name: 'yearcompass-storage',
      version: 1,
      onRehydrateStorage: (state) => {
        console.log('[Store] 开始持久化数据恢复...')
        return (state, error) => {
          if (error) {
            console.error('[Store] 持久化恢复出错:', error)
          } else {
            console.log('[Store] 持久化数据恢复完成')
            state.setHasHydrated(true)
          }
        }
      }
    }
  )
)
